import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// All requests include cookies (httpOnly auth token)
axios.defaults.withCredentials = true;

export const api = {
  // Vehicles
  getVehicles: async (status) => {
    const response = await axios.get(`${API_URL}/api/vehicles`, { 
      params: status ? { status } : {}
    });
    return response.data;
  },
  
  getVehicle: async (id) => {
    const response = await axios.get(`${API_URL}/api/vehicles/${id}`);
    return response.data;
  },
  
  createVehicle: async (data) => {
    const response = await axios.post(`${API_URL}/api/vehicles`, data);
    return response.data;
  },
  
  createVehiclesBulk: async (data) => {
    const response = await axios.post(`${API_URL}/api/vehicles/bulk`, data);
    return response.data;
  },
  
  updateVehicle: async (id, data) => {
    const response = await axios.put(`${API_URL}/api/vehicles/${id}`, data);
    return response.data;
  },
  
  deleteVehicle: async (id) => {
    const response = await axios.delete(`${API_URL}/api/vehicles/${id}`);
    return response.data;
  },
  
  getDriverFuelRanking: async () => {
    const response = await axios.get(`${API_URL}/api/drivers/fuel-ranking`);
    return response.data;
  },

  checkReading: async (id, body) => {
    const response = await axios.post(`${API_URL}/api/vehicles/${id}/check-reading`, body);
    return response.data;
  },

  getUsageRate: async (id) => {
    const response = await axios.get(`${API_URL}/api/vehicles/${id}/usage-rate`);
    return response.data;
  },

  recalculateServices: async () => {
    const response = await axios.post(`${API_URL}/api/vehicles/recalculate-services`);
    return response.data;
  },

  markVehicleServiced: async (id, odometer, serviceDate) => {
    const response = await axios.post(
      `${API_URL}/api/vehicles/${id}/mark-serviced`,
      null,
      { 
          params: { odometer, service_date: serviceDate }
      }
    );
    return response.data;
  },
  
  // Drivers
  getDrivers: async (status) => {
    const response = await axios.get(`${API_URL}/api/drivers`, { 
      params: status ? { status } : {}
    });
    return response.data;
  },
  
  getDriver: async (id) => {
    const response = await axios.get(`${API_URL}/api/drivers/${id}`);
    return response.data;
  },
  
  createDriver: async (data) => {
    const response = await axios.post(`${API_URL}/api/drivers`, data);
    return response.data;
  },
  
  updateDriver: async (id, data) => {
    const response = await axios.put(`${API_URL}/api/drivers/${id}`, data);
    return response.data;
  },
  
  deleteDriver: async (id) => {
    const response = await axios.delete(`${API_URL}/api/drivers/${id}`);
    return response.data;
  },
  
  // Costs
  getCosts: async (params = {}) => {
    const response = await axios.get(`${API_URL}/api/costs`, { 
      params 
    });
    return response.data;
  },

  checkDuplicate: async (data) => {
    const response = await axios.post(`${API_URL}/api/costs/check-duplicate`, data);
    return response.data;
  },
  
  createCost: async (data, force = false) => {
    const response = await axios.post(`${API_URL}/api/costs`, data, { 
      params: force ? { force: true } : {}
    });
    return response.data;
  },
  
  updateCost: async (id, data) => {
    const response = await axios.put(`${API_URL}/api/costs/${id}`, data);
    return response.data;
  },
  
  deleteCost: async (id) => {
    const response = await axios.delete(`${API_URL}/api/costs/${id}`);
    return response.data;
  },

  downloadCostTemplate: async () => {
    const response = await axios.get(`${API_URL}/api/costs/import/template`, { responseType: 'blob' });
    return response.data;
  },

  uploadCostImport: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/api/costs/import/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  
  // Suppliers
  getSuppliers: async (supplier_type) => {
    const response = await axios.get(`${API_URL}/api/suppliers`, { 
      params: supplier_type ? { supplier_type } : {}
    });
    return response.data;
  },
  
  getSupplier: async (id) => {
    const response = await axios.get(`${API_URL}/api/suppliers/${id}`);
    return response.data;
  },
  
  createSupplier: async (data) => {
    const response = await axios.post(`${API_URL}/api/suppliers`, data);
    return response.data;
  },
  
  updateSupplier: async (id, data) => {
    const response = await axios.put(`${API_URL}/api/suppliers/${id}`, data);
    return response.data;
  },
  
  deleteSupplier: async (id) => {
    const response = await axios.delete(`${API_URL}/api/suppliers/${id}`);
    return response.data;
  },
  
  getSupplierReport: async (id, params = {}) => {
    const response = await axios.get(`${API_URL}/api/suppliers/${id}/report`, { 
      params 
    });
    return response.data;
  },
  
  // Alerts
  dismissAlert: async (alertKey) => {
    const response = await axios.post(`${API_URL}/api/alerts/dismiss`, { alert_key: alertKey });
    return response.data;
  },

  updateVehicleLicense: async (vehicleId, newLicenseExpiry) => {
    const response = await axios.post(`${API_URL}/api/vehicles/${vehicleId}/update-license`, { vehicle_id: vehicleId, new_license_expiry: newLicenseExpiry }, {});
    return response.data;
  },

  stopImpersonating: async () => {
    const response = await axios.post(`${API_URL}/api/platform-admin/stop-impersonating`);
    return response.data;
  },

  markVehicleSold: async (vehicleId, payload) => {
    const r = await axios.post(`${API_URL}/api/vehicles/${vehicleId}/mark-sold`, payload || {});
    return r.data;
  },
  unmarkVehicleSold: async (vehicleId) => {
    const r = await axios.post(`${API_URL}/api/vehicles/${vehicleId}/unmark-sold`);
    return r.data;
  },

  // Compliance
  getFleetCompliance: async () => {
    const response = await axios.get(`${API_URL}/api/compliance`);
    return response.data;
  },

  downloadCompliancePDF: async (vehicleId) => {
    const response = await axios.get(`${API_URL}/api/compliance/${vehicleId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  getComplianceHistory: async (vehicleId = null) => {
    const params = vehicleId ? { vehicle_id: vehicleId } : {};
    const response = await axios.get(`${API_URL}/api/compliance/history`, { params });
    return response.data;
  },

  // Incidents
  getIncidents: async (vehicleId = null, status = null) => {
    const params = {};
    if (vehicleId) params.vehicle_id = vehicleId;
    if (status) params.status = status;
    const response = await axios.get(`${API_URL}/api/incidents`, { params });
    return response.data;
  },

  createIncident: async (data) => {
    const response = await axios.post(`${API_URL}/api/incidents`, data);
    return response.data;
  },

  updateIncident: async (id, data) => {
    const response = await axios.put(`${API_URL}/api/incidents/${id}`, data);
    return response.data;
  },

  deleteIncident: async (id) => {
    const response = await axios.delete(`${API_URL}/api/incidents/${id}`);
    return response.data;
  },

  getIncidentsByDriver: async (startDate = null, endDate = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await axios.get(`${API_URL}/api/reports/incidents-by-driver`, { params });
    return response.data;
  },

  // Trips
  getTrips: async (vehicleId = null, driverId = null) => {
    const params = {};
    if (vehicleId) params.vehicle_id = vehicleId;
    if (driverId) params.driver_id = driverId;
    const response = await axios.get(`${API_URL}/api/trips`, { params });
    return response.data;
  },

  createTrip: async (data) => {
    const response = await axios.post(`${API_URL}/api/trips`, data);
    return response.data;
  },

  updateTrip: async (id, data) => {
    const response = await axios.put(`${API_URL}/api/trips/${id}`, data);
    return response.data;
  },

  deleteTrip: async (id) => {
    const response = await axios.delete(`${API_URL}/api/trips/${id}`);
    return response.data;
  },

  // Vehicle Documents
  getVehicleDocuments: async (vehicleId) => {
    const response = await axios.get(`${API_URL}/api/vehicles/${vehicleId}/documents`);
    return response.data;
  },

  uploadVehicleDocument: async (vehicleId, formData) => {
    const response = await axios.post(`${API_URL}/api/vehicles/${vehicleId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  deleteVehicleDocument: async (vehicleId, docId) => {
    const response = await axios.delete(`${API_URL}/api/vehicles/${vehicleId}/documents/${docId}`);
    return response.data;
  },

  // Inspections
  getInspections: async (vehicleId = null) => {
    const params = vehicleId ? { vehicle_id: vehicleId } : {};
    const response = await axios.get(`${API_URL}/api/inspections`, { params });
    return response.data;
  },

  createInspection: async (data) => {
    const response = await axios.post(`${API_URL}/api/inspections`, data);
    return response.data;
  },

  deleteInspection: async (id) => {
    const response = await axios.delete(`${API_URL}/api/inspections/${id}`);
    return response.data;
  },

  // Dashboard
  getDashboardStats: async (period = 'month') => {
    const response = await axios.get(`${API_URL}/api/dashboard/stats`, { 
      params: { period }
    });
    return response.data;
  },
  
  // Reports
  getReportSummary: async (params = {}) => {
    const response = await axios.get(`${API_URL}/api/reports/summary`, { 
      params 
    });
    return response.data;
  },
  
  exportPDF: async (params = {}) => {
    const response = await axios.get(`${API_URL}/api/reports/export/pdf`, { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },
  
  exportExcel: async (params = {}) => {
    const response = await axios.get(`${API_URL}/api/reports/export/excel`, { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  exportVehiclesPDF: async () => {
    const response = await axios.get(`${API_URL}/api/reports/export/vehicles/pdf`, { responseType: 'blob' });
    return response.data;
  },

  exportVehiclesExcel: async () => {
    const response = await axios.get(`${API_URL}/api/reports/export/vehicles/excel`, { responseType: 'blob' });
    return response.data;
  },

  // Invoice scanning
  scanInvoice: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/api/invoices/scan`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },

  confirmInvoice: async (data, force = false) => {
    const response = await axios.post(`${API_URL}/api/invoices/confirm`, data, {
      params: force ? { force: true } : {},
    });
    return response.data;
  },

  getInvoiceScans: async () => {
    const response = await axios.get(`${API_URL}/api/invoices/scans`);
    return response.data;
  },

  // Email Report Settings
  getEmailReportSettings: async () => {
    const response = await axios.get(`${API_URL}/api/settings/email-report`);
    return response.data;
  },

  updateEmailReportSettings: async (enabled) => {
    const response = await axios.post(`${API_URL}/api/settings/email-report`, { enabled });
    return response.data;
  },

  // Driver Scorecards
  getScorecards: async (vehicleId = null) => {
    const params = vehicleId ? { vehicle_id: vehicleId } : {};
    const response = await axios.get(`${API_URL}/api/scorecards`, { params });
    return response.data;
  },

  createScorecard: async (data) => {
    const response = await axios.post(`${API_URL}/api/scorecards`, data);
    return response.data;
  },

  deleteScorecard: async (id) => {
    const response = await axios.delete(`${API_URL}/api/scorecards/${id}`);
    return response.data;
  },
};
